/**
 * Plain-language text of a change-request approval preview
 * (`GET /api/v1/change-requests/:id/approval-preview`). The server sends each
 * line as a stable code with parameters; this module turns it into one
 * sentence an office clerk understands ("Creates a new contact: ..."). It also
 * names the Approve button by what it does ("Approve and add contact").
 */

import type { I18n } from '@lingui/core';
import { getContactFieldLabel, getWeekdayLabel, isContactPrivacySubType } from '@/types/changeRequest';
import { formatMoneyCents } from '@/lib/money';
import { formatRequestDate } from './changeRequestParentSummaries';

export interface PreviewLine {
  code: string;
  params?: Record<string, string>;
}

export interface PreviewTextContext {
  i18n: I18n;
  /** Service code -> name ("FULL_DAY" -> "Ganzer Tag"). */
  serviceLabel: (code: string) => string;
  /** Relationship enum -> label ("GRANDPARENT" -> "Grandparent"). */
  relationshipLabel: (type: string) => string;
}

function p(line: PreviewLine, key: string): string {
  return line.params?.[key] ?? '';
}

/** "EMERGENCY_CONTACT,PICKUP_ALLOWED" -> "emergency contact, may pick up the child". */
export function rolesText(i18n: I18n, raw: string): string {
  return raw
    .split(',')
    .filter(Boolean)
    .map((role) => {
      switch (role) {
        case 'EMERGENCY_CONTACT':
          return i18n._({ id: 'approvalPreview.role.emergency', message: 'emergency contact' });
        case 'PICKUP_ALLOWED':
          return i18n._({ id: 'approvalPreview.role.pickup', message: 'may pick up the child' });
        default:
          return role;
      }
    })
    .join(', ');
}

/** "1=FULL_DAY;3=LUNCH+MORNING" -> "Monday: Full day · Wednesday: Lunch + Morning". */
export function weekText(i18n: I18n, raw: string, serviceLabel: (code: string) => string): string {
  if (!raw) return i18n._({ id: 'approvalPreview.week.none', message: 'no care days' });
  return raw
    .split(';')
    .map((part) => {
      const [day, codes = ''] = part.split('=');
      return i18n._({
        id: 'approvalPreview.week.day',
        message: '{day}: {services}',
        values: {
          day: getWeekdayLabel(i18n, Number(day)),
          services: codes.split('+').filter(Boolean).map(serviceLabel).join(' + '),
        },
      });
    })
    .join(' · ');
}

/** "2026-11,2026-12" -> "November 2026, December 2026" in the active locale. */
export function monthsText(raw: string, locale: string): string {
  const format = new Intl.DateTimeFormat(locale || 'en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return raw
    .split(',')
    .filter(Boolean)
    .map((ym) => {
      const [year, month] = ym.split('-').map(Number);
      if (!year || !month) return ym;
      return format.format(new Date(Date.UTC(year, month - 1, 1)));
    })
    .join(', ');
}

/** Child master-data field name ("birthdate" -> "Date of birth"). */
/** "2026-10-12,2026-10-13" -> the dates in the locale's format, comma separated. */
export function datesText(raw: string, locale: string): string {
  return raw
    .split(',')
    .filter(Boolean)
    .map((value) => formatRequestDate(value, locale))
    .join(', ');
}

export function childFieldLabel(i18n: I18n, field: string): string {
  switch (field) {
    case 'firstname':
      return i18n._({ id: 'approvalPreview.childField.firstname', message: 'First name' });
    case 'lastname':
      return i18n._({ id: 'approvalPreview.childField.lastname', message: 'Last name' });
    case 'birthdate':
      return i18n._({ id: 'approvalPreview.childField.birthdate', message: 'Date of birth' });
    case 'gender':
      return i18n._({ id: 'approvalPreview.childField.gender', message: 'Gender' });
    case 'nationalities':
      return i18n._({ id: 'approvalPreview.childField.nationalities', message: 'Nationality' });
    default:
      return field;
  }
}

/** "CH,DE" -> "Switzerland, Germany" in the UI language; unknown codes stay as they are. */
export function countriesText(raw: string, locale: string): string {
  if (!raw) return '';
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    names = null;
  }
  return raw
    .split(',')
    .map((code) => names?.of(code) ?? code)
    .join(', ');
}

/** A child master-data value in plain words (date, gender, countries). */
export function childValueText(i18n: I18n, field: string, value: string): string {
  if (!value) return '';
  switch (field) {
    case 'birthdate':
      return formatRequestDate(value, i18n.locale);
    case 'gender':
      switch (value) {
        case 'MALE':
          return i18n._({ id: 'approvalPreview.gender.male', message: 'male' });
        case 'FEMALE':
          return i18n._({ id: 'approvalPreview.gender.female', message: 'female' });
        case 'OTHER':
          return i18n._({ id: 'approvalPreview.gender.other', message: 'other' });
        default:
          return value;
      }
    case 'nationalities':
      return countriesText(value, i18n.locale);
    default:
      return value;
  }
}

/**
 * A `children` / `basic` request body as one line per field:
 * "Date of birth: 14.03.2022; Nationality: Switzerland, Germany".
 */
export function basicChangeText(basic: Record<string, unknown>, i18n: I18n): string {
  const fields = ['firstname', 'lastname', 'birthdate', 'gender', 'nationalities'];
  return fields
    .map((field) => {
      const raw = basic[field];
      const value = Array.isArray(raw) ? raw.filter((v) => typeof v === 'string').join(',') : typeof raw === 'string' ? raw : '';
      return value ? `${childFieldLabel(i18n, field)}: ${childValueText(i18n, field, value)}` : '';
    })
    .filter(Boolean)
    .join('; ');
}

/** One preview line as a sentence. Unknown codes fall back to the server's detail text. */
export function previewLineText(line: PreviewLine, ctx: PreviewTextContext): string {
  const { i18n } = ctx;
  const date = (key: string) => formatRequestDate(p(line, key), i18n.locale);
  switch (line.code) {
    case 'no_change':
      return i18n._({ id: 'approvalPreview.noChange', message: 'Nothing changes: the data already matches the request.' });

    // New contact (child_contact)
    case 'contact.create':
      return p(line, 'email')
        ? i18n._({
            id: 'approvalPreview.contactCreateWithEmail',
            message: 'Creates a new contact: {name}, mobile {phone}, e-mail {email}.',
            values: { name: p(line, 'name'), phone: p(line, 'phone'), email: p(line, 'email') },
          })
        : i18n._({
            id: 'approvalPreview.contactCreate',
            message: 'Creates a new contact: {name}, mobile {phone}.',
            values: { name: p(line, 'name'), phone: p(line, 'phone') },
          });
    case 'contact.link_existing':
      return i18n._({
        id: 'approvalPreview.contactLinkExisting',
        message: 'Uses the existing contact {name} with the same e-mail address ({email}). No second person is created.',
        values: { name: p(line, 'name'), email: p(line, 'email') },
      });
    case 'contact.name_differs':
      return i18n._({
        id: 'approvalPreview.contactNameDiffers',
        message: 'Check the person: the parent wrote "{requested}", but the contact with this e-mail address is called "{existing}".',
        values: { requested: p(line, 'requested'), existing: p(line, 'existing') },
      });
    case 'relationship.create':
      return i18n._({
        id: 'approvalPreview.relationshipCreate',
        message: 'Links the contact to {child} as {relationship}, with these rights: {roles}.',
        values: {
          child: p(line, 'child'),
          relationship: ctx.relationshipLabel(p(line, 'relationship')),
          roles: rolesText(i18n, p(line, 'roles')),
        },
      });
    case 'relationship.add_roles':
      return i18n._({
        id: 'approvalPreview.relationshipAddRoles',
        message: '{name} is already linked to {child}. Adds these rights: {roles}.',
        values: { name: p(line, 'name'), child: p(line, 'child'), roles: rolesText(i18n, p(line, 'roles')) },
      });

    // Privacy requests (contact_data, #1876): the office acts outside the app,
    // then approves; approving changes no data.
    case 'contact.data_access_request':
      return i18n._({
        id: 'approvalPreview.contact.dataAccessRequest',
        message: 'Send the family a copy of their data, then approve.',
      });
    case 'contact.account_deletion_request':
      return i18n._({
        id: 'approvalPreview.contact.accountDeletionRequest',
        message: 'Revoke the login and unlink the contact, then approve.',
      });

    // Parent's own contact details (contact_data)
    case 'contact.update': {
      const field = getContactFieldLabel(i18n, p(line, 'field'));
      if (!p(line, 'old')) {
        return i18n._({
          id: 'approvalPreview.contactSet',
          message: 'Sets {field} to "{value}".',
          values: { field, value: p(line, 'new') },
        });
      }
      if (!p(line, 'new')) {
        return i18n._({
          id: 'approvalPreview.contactClear',
          message: 'Removes {field} (was "{value}").',
          values: { field, value: p(line, 'old') },
        });
      }
      return i18n._({
        id: 'approvalPreview.contactUpdate',
        message: 'Changes {field} from "{old}" to "{value}".',
        values: { field, old: p(line, 'old'), value: p(line, 'new') },
      });
    }

    // Child master data (children / basic)
    case 'child.update': {
      const field = p(line, 'field');
      const label = childFieldLabel(i18n, field);
      const next = childValueText(i18n, field, p(line, 'new'));
      if (!p(line, 'old')) {
        return i18n._({
          id: 'approvalPreview.childSet',
          message: 'Sets {field} to "{value}".',
          values: { field: label, value: next },
        });
      }
      return i18n._({
        id: 'approvalPreview.childUpdate',
        message: 'Changes {field} from "{old}" to "{value}".',
        values: { field: label, old: childValueText(i18n, field, p(line, 'old')), value: next },
      });
    }
    case 'child.invalid':
      return i18n._({
        id: 'approvalPreview.blocker.childInvalid',
        message: 'The requested details are not valid. Reject the request and ask the parent to send it again.',
      });

    // Service days (contract / care_days)
    case 'contract.temporary_version':
      return i18n._({
        id: 'approvalPreview.contractTemporary',
        message: 'Changes the contract for {from} to {to} only. Week in this period: {newWeek}. Before and after: {oldWeek}.',
        values: {
          from: date('from'),
          to: date('to'),
          newWeek: weekText(i18n, p(line, 'new_week'), ctx.serviceLabel),
          oldWeek: weekText(i18n, p(line, 'old_week'), ctx.serviceLabel),
        },
      });
    case 'booking.temporary':
      return i18n._({
        id: 'approvalPreview.bookingTemporary',
        message: 'Books the child in group {group} with this week from {from} to {to}. Before and after, the booking stays as it is.',
        values: { group: p(line, 'group'), from: date('from'), to: date('to') },
      });
    case 'contract.new_version':
      return i18n._({
        id: 'approvalPreview.contractNewVersion',
        message: 'Changes the contract from {from}. New week: {newWeek}. Until then: {oldWeek}.',
        values: {
          from: date('from'),
          newWeek: weekText(i18n, p(line, 'new_week'), ctx.serviceLabel),
          oldWeek: weekText(i18n, p(line, 'old_week'), ctx.serviceLabel),
        },
      });
    case 'contract.later_version_changed':
      return i18n._({
        id: 'approvalPreview.contractLaterChanged',
        message: 'Also uses the new week in the contract version that starts on {from}.',
        values: { from: date('from') },
      });
    case 'contract.later_versions_kept':
      return i18n._({
        id: 'approvalPreview.contractLaterKept',
        message: '{count} later contract version(s) have their own week (for example a pause) and stay as they are. Check them after approving.',
        values: { count: p(line, 'count') },
      });
    case 'booking.continue':
      return i18n._({
        id: 'approvalPreview.bookingContinue',
        message: 'Books the child in group {group} with the new week from {from}. The booking before that date stays as it is.',
        values: { group: p(line, 'group'), from: date('from') },
      });
    case 'booking.update':
      return i18n._({
        id: 'approvalPreview.bookingUpdate',
        message: 'Changes the booking in group {group} from {from} to the new week.',
        values: { group: p(line, 'group'), from: date('from') },
      });
    case 'booking.missing':
      return i18n._({
        id: 'approvalPreview.bookingMissing',
        message: 'The child has no group booking from the start date. After approving, book the child on the planning page.',
      });
    case 'invoices.rebill':
      return i18n._({
        id: 'approvalPreview.invoicesRebill',
        message: 'Recalculates the invoices for: {months}. Invoices that are already sent are cancelled and issued again.',
        values: { months: monthsText(p(line, 'months'), i18n.locale) },
      });

    // Extra days after the contract end (#1977)
    case 'extra_days.book':
      return p(line, 'rate')
        ? i18n._({
            id: 'approvalPreview.extraDaysBook',
            message:
              'Books {dates} as single extra days ({service}) in group {group} at the extra-day rate of {rate} per day. The contract ends on {end} and is not extended.',
            values: {
              end: date('end'),
              dates: datesText(p(line, 'dates'), i18n.locale),
              service: ctx.serviceLabel(p(line, 'service')),
              group: p(line, 'group'),
              rate: formatMoneyCents(Number(p(line, 'rate')), p(line, 'currency')),
            },
          })
        : i18n._({
            id: 'approvalPreview.extraDaysBookNoRate',
            message:
              'Books {dates} as single extra days ({service}) in group {group}. The contract ends on {end} and is not extended.',
            values: {
              end: date('end'),
              dates: datesText(p(line, 'dates'), i18n.locale),
              service: ctx.serviceLabel(p(line, 'service')),
              group: p(line, 'group'),
            },
          });
    case 'extra_days.no_tariff':
      return i18n._({
        id: 'approvalPreview.extraDaysNoTariff',
        message:
          'The contract has no extra-day tariff for {service}. The extra days on {dates} are billed at 0.00 until such a tariff exists.',
        values: { service: ctx.serviceLabel(p(line, 'service')), dates: datesText(p(line, 'dates'), i18n.locale) },
      });
    case 'invoices.extras_only':
      return i18n._({
        id: 'approvalPreview.invoicesExtrasOnly',
        message:
          'Bills the extra days in {months} on an invoice with the extra days only, because the contract no longer runs then. A month that has not started yet is billed by the regular invoice run.',
        values: { months: monthsText(p(line, 'months'), i18n.locale) },
      });

    // Pause and termination (contract)
    case 'contract.pause':
      return i18n._({
        id: 'approvalPreview.contractPause',
        message: 'Pauses the contract from {from} to {to}: no care days in this period.',
        values: { from: date('from'), to: date('to') },
      });
    case 'termination.open':
      return i18n._({
        id: 'approvalPreview.terminationOpen',
        message: 'Opens the termination of the contract with the last day of care {date}. The termination case starts as approved.',
        values: { date: date('last_day') },
      });
    case 'termination.already_open':
      return i18n._({
        id: 'approvalPreview.terminationAlreadyOpen',
        message: 'A termination case is already open for this contract. Approving links this request to it; no second case is opened.',
      });
    case 'termination.next_step':
      return i18n._({
        id: 'approvalPreview.terminationNextStep',
        message: 'Next step: open the termination case and confirm the exit fees and the invoices. The contract ends only then.',
      });

    // Group change (group_allocation)
    case 'booking.move':
      return p(line, 'from_group')
        ? i18n._({
            id: 'approvalPreview.bookingMove',
            message: 'Moves the child from group {fromGroup} to group {toGroup} from {date}. The booked days stay the same.',
            values: { fromGroup: p(line, 'from_group'), toGroup: p(line, 'to_group'), date: date('date') },
          })
        : i18n._({
            id: 'approvalPreview.bookingMoveNoFrom',
            message: 'Moves the child to group {toGroup} from {date}. The booked days stay the same.',
            values: { toGroup: p(line, 'to_group'), date: date('date') },
          });
    case 'capacity.breach':
      return i18n._({
        id: 'approvalPreview.capacityBreach',
        message: 'Group capacity for {service} is at {percent}% after the move.',
        values: { service: p(line, 'service'), percent: p(line, 'percent') },
      });

    // Blockers
    case 'group.choose':
      return i18n._({
        id: 'approvalPreview.blocker.groupChoose',
        message: 'No group is named "{name}". Choose the target group above.',
        values: { name: p(line, 'requested') },
      });
    case 'group.not_found':
      return i18n._({ id: 'approvalPreview.blocker.groupNotFound', message: 'The chosen group no longer exists. Choose another group.' });
    case 'booking.none':
      return i18n._({ id: 'approvalPreview.blocker.bookingNone', message: 'The child has no group booking on that date that could be moved.' });
    case 'booking.double':
      return i18n._({ id: 'approvalPreview.blocker.bookingDouble', message: 'The child is already booked in another group at that time.' });
    case 'group.move_invalid':
      return i18n._({ id: 'approvalPreview.blocker.moveInvalid', message: 'The child cannot be moved on that date.' });
    case 'child.not_found':
      return i18n._({ id: 'approvalPreview.blocker.childNotFound', message: 'The child no longer exists.' });
    case 'service_days.unknown_service':
      return i18n._({
        id: 'approvalPreview.blocker.unknownService',
        message: 'The parent chose a service that the daycare no longer offers. Reject the request and ask the parent to send it again.',
      });
    case 'contract.not_running':
      return i18n._({ id: 'approvalPreview.blocker.notRunning', message: 'The contract does not run on the requested start date.' });
    case 'contract.before_start':
      return i18n._({ id: 'approvalPreview.blocker.beforeStart', message: 'The requested start date is before the contract starts.' });
    case 'contract.no_group_type':
      return i18n._({
        id: 'approvalPreview.blocker.noGroupType',
        message: 'The contract has no booked services yet. Book the services on the contract first.',
      });
    case 'contract.ends_in_window':
      return i18n._({
        id: 'approvalPreview.blocker.endsInWindow',
        message: 'The contract ends before the end of the requested period. Agree the dates with the parent and change the contract by hand.',
      });
    case 'extra_days.no_group':
      return i18n._({
        id: 'approvalPreview.blocker.extraDaysNoGroup',
        message: 'The child has no group booking on this contract to book the extra days in. Book the child on the planning page first.',
      });
    case 'contract.changes_in_window':
      return i18n._({
        id: 'approvalPreview.blocker.changesInWindow',
        message: 'The contract already changes within the requested period. Change the contract by hand.',
      });
    case 'contract.too_many_days':
      return i18n._({ id: 'approvalPreview.blocker.tooManyDays', message: 'The contract allows at most 5 care days per week.' });
    case 'pause.overlap':
      return i18n._({
        id: 'approvalPreview.blocker.pauseOverlap',
        message: 'The contract already has a pause in this period. Change or cancel that pause first.',
      });
    case 'pause.invalid':
      return i18n._({
        id: 'approvalPreview.blocker.pauseInvalid',
        message: 'The pause cannot be applied to this contract. Check the contract and the dates.',
      });
    case 'termination.unavailable':
      return i18n._({
        id: 'approvalPreview.blocker.terminationUnavailable',
        message: 'The termination cannot be opened automatically. Start it on the contract.',
      });
    case 'service_days.invalid':
    case 'contract.invalid':
    case 'contact.invalid':
    case 'group.invalid':
      return i18n._({
        id: 'approvalPreview.blocker.invalid',
        message: 'The request is incomplete. Reject it and ask the parent to send it again.',
      });
    default:
      return p(line, 'detail')
        ? i18n._({
            id: 'approvalPreview.blocker.detail',
            message: 'This request cannot be applied: {detail}',
            values: { detail: p(line, 'detail') },
          })
        : line.code;
  }
}

/**
 * The Approve button names the result, so the reviewer knows what one click
 * does. Families without a specific label keep "Approve". `actionCodes` are the
 * preview's action codes when known: a care-days request that only books extra
 * days after the contract end (#1977) changes no contract.
 */
export function approveActionLabel(i18n: I18n, family: string, subType: string, actionCodes: string[] = []): string {
  switch (family) {
    case 'child_contact':
      return i18n._({ id: 'approvalPreview.approve.childContact', message: 'Approve and add contact' });
    case 'contact_data':
      // A privacy request changes no contact data: the button stays "Approve".
      if (isContactPrivacySubType(subType)) break;
      return i18n._({ id: 'approvalPreview.approve.contactData', message: 'Approve and update contact details' });
    case 'children':
      return i18n._({ id: 'approvalPreview.approve.children', message: 'Approve and update child data' });
    case 'group_allocation':
      return i18n._({ id: 'approvalPreview.approve.groupAllocation', message: 'Approve and move child' });
    case 'contract':
      switch (subType) {
        case 'care_days':
          if (actionCodes.includes('extra_days.book') && !actionCodes.some((code) => code.startsWith('contract.'))) {
            return i18n._({ id: 'approvalPreview.approve.extraDays', message: 'Approve and book extra days' });
          }
          return i18n._({ id: 'approvalPreview.approve.careDays', message: 'Approve and change contract' });
        case 'pause':
          return i18n._({ id: 'approvalPreview.approve.pause', message: 'Approve and pause contract' });
        case 'terminate':
          return i18n._({ id: 'approvalPreview.approve.terminate', message: 'Approve and open termination' });
      }
      break;
  }
  return i18n._({ id: 'changeRequest.triage.approve', message: 'Approve' });
}
